import { useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import type { ActionBandeau } from '../../lib/solutionRegistry'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/platform/components/ui/dropdown-menu'

const STYLE_DATE = { fontFamily: 'var(--font-body)', fontSize: 'var(--fs-small)', color: 'hsl(var(--primary))', fontWeight: 600 } as const

function IconeCalendrier() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="1" y="2" width="14" height="13" rx="1"/>
      <path d="M1 6h14M5 1v3M11 1v3"/>
    </svg>
  )
}

/**
 * Date du dernier audit, dans le bandeau. Spec 160 (FR-018) : sous 1 280 px, le libellé « Dernier audit : » passe en
 * texte masqué (lu par les lecteurs d'écran, repris en infobulle) ; la date reste visible.
 *
 * Spec 161, second retour de Christophe sur `dev` (2026-10-12) : quand une solution propose des actions pour le client
 * affiché (« Lancer l'audit », « Suivre l'audit »), la date ouvre un menu. Sans action — le client, la Vue client —, elle
 * reste du texte : rien à ouvrir. L'action choisie part APRÈS la fermeture du menu ; la boîte de confirmation qu'elle
 * ouvre donne le focus à « Annuler » (règle de design, spec 160), et le rend à la date en se fermant.
 */
export function DateDernierAudit({ date, ecranLarge, actions }: { date: string; ecranLarge: boolean; actions: ActionBandeau[] }) {
  const actionChoisie = useRef<(() => void) | null>(null)
  const declencheur = useRef<HTMLButtonElement>(null)
  const libelle = (
    <span className={ecranLarge ? undefined : 'sr-only'} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-small)' }}>
      Dernier audit :
    </span>
  )
  const valeur = <span title={ecranLarge ? undefined : `Dernier audit : ${date}`} style={STYLE_DATE}>{date}</span>

  if (actions.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, color: 'hsl(var(--foreground))' }}>
        <IconeCalendrier />{libelle}{valeur}
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        ref={declencheur}
        aria-label={`Dernier audit : ${date} — actions d'audit`}
        className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-muted"
        style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, color: 'hsl(var(--foreground))', background: 'transparent', border: 'none', padding: '4px 6px', cursor: 'pointer' }}
      >
        <IconeCalendrier />{libelle}{valeur}<ChevronDown size={14} aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        onCloseAutoFocus={e => {
          const action = actionChoisie.current
          if (!action) return
          actionChoisie.current = null
          e.preventDefault()
          // Le focus revient à la date AVANT que l'action ouvre sa boîte : c'est là que la boîte le rendra en se
          // fermant (sinon il tombait sur la page). La boîte, elle, le donne aussitôt à « Annuler ».
          declencheur.current?.focus()
          action()
        }}
      >
        {actions.map(a => (
          <DropdownMenuItem key={a.id} disabled={a.desactivee} onSelect={() => { actionChoisie.current = a.onSelect }}>
            {a.libelle}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
